/** Environment configuration utilities. `resolveEnvironment()` is the CANONICAL environment resolver for the monorepo -- every other environment-detection function delegates to it. Server priority chain: NEXT_PUBLIC_APP_ENV > NODE_ENV > defaultEnv; client reads `window.__ENV_CONFIG__` then hostname matching. */

import { ENVIRONMENTS } from '@federa/const/config-environments';
import { DEPLOY_ENV_TARGETS, toDeployEnv } from '@federa/const/config-deploy-targets';
import { ACTIVE_BRAND } from '@federa/const/brand-registry';
import { FEDERA_DEV_URL, FEDERA_PROD_URL, STORAGE_GATEWAY } from '@federa/const/urls-connect';
import { ENV_FEDERA } from '@federa/const/env-var-names';
import { ENV_NEXT_PUBLIC } from '@federa/const/env-var-names-app-config';
import { HOSTNAME_ENV_MARKER } from '@federa/const/hostname-environment-markers';
import type { DeployEnv, EnvironmentType } from '@federa/types/wrangler-deploy-env';

// Module-scoped browser type declaration (Workers tsconfig has no DOM lib); the `undefined` variant backs the inline SSR/browser guard below.
declare const window: undefined | { location?: { hostname?: string }; __ENV_CONFIG__?: { environment?: string } };

/** Normalize a raw env string to the production/development enum (null when empty). */
function normalizeEnvString(raw: string | undefined): EnvironmentType | null {
	if (!raw) return null;
	const lower = raw.toLowerCase();
	return lower === ENVIRONMENTS.PRODUCTION || lower === DEPLOY_ENV_TARGETS.PROD ? ENVIRONMENTS.PRODUCTION : ENVIRONMENTS.DEVELOPMENT;
}

/** Priority-chain env getter: first defined non-empty value among `keys`, else `defaultValue`. `keys` stays `readonly string[]` (variance pin in test). */
export function getEnv(keys: readonly string[], defaultValue: string = ''): string {
	for (const key of keys) {
		const value = process.env[key];
		if (value !== undefined && value !== '') {
			return value;
		}
	}

	return defaultValue;
}

/** Canonical SCAN-ENV resolver (compliance scan, resource-schema build, scanner rule helpers): the FlowRunner CLI (`tools/flowrunner-cli/run-cli.ts`) publishes its `--env` into `FEDERA_FLOW_ENV` while parsing argv (the flag is `FLOW_RUNNER_ENV_FLAG`, `const/cli-flowrunner.ts`), and worker_threads isolates inherit the process-env copy, so every scanner surface reads ONE authority. Unset (tests, bare node) reads as dev, matching the CLI default.
 *
 * THE PUBLISHER WENT MISSING FOR FIVE DAYS AND THIS DOCSTRING DID NOT SAY SO. It cited resolveFlowRunnerEnvName, a symbol the 2026-08-13 worker conversion deleted along with the CLI's `--env`; from then until 2026-08-18 nothing anywhere wrote the name, so every caller here silently resolved `dev` while the citation still described a live mechanism. Re-pointed at the flag const that publishes it today. */
export function resolveScanDeployEnv(): DeployEnv {
	return toDeployEnv(getEnv([ENV_FEDERA.FLOW_ENV], ''));
}

/** Canonical browser-side production-hostname check. False for `localhost`, any subdomain whose leftmost label is `dev` or ends with `-dev`, and any non-production hostname. Use this instead of re-rolling a `host.includes('dev')` check. */
export function isProductionHostname(hostname: string): boolean {
	// The apex is READ from the active brand, never spelled: this check decides PRODUCTION vs development for the whole
	// client, so a rebrand that left a literal here would classify every real production host as development.
	const apex = ACTIVE_BRAND.domains.primary;
	if (hostname === apex) return true;
	if (hostname.endsWith(`.${apex}`)) {
		const leftmost = hostname.slice(0, hostname.length - apex.length - 1).split('.')[0] ?? '';
		const isDevSubdomain = leftmost === 'dev' || leftmost.endsWith(HOSTNAME_ENV_MARKER.DEV_SUBDOMAIN_SUFFIX);
		if (!isDevSubdomain) return true;
	}
	if (hostname.includes(HOSTNAME_ENV_MARKER.PRODUCTION_SUBSTRING)) return true;
	return false;
}

/** Canonical environment resolver (priority chain in the module docstring); returns the normalized 'development' or 'production'. */
export function resolveEnvironment(defaultEnv: EnvironmentType = ENVIRONMENTS.DEVELOPMENT): EnvironmentType {
	// Client-side: read from window.__ENV_CONFIG__ (injected by root layout).
	if (typeof window !== 'undefined') {
		const fromConfig = normalizeEnvString(window.__ENV_CONFIG__?.environment);
		if (fromConfig) return fromConfig;

		const hostname = window.location?.hostname ?? '';
		return isProductionHostname(hostname) ? ENVIRONMENTS.PRODUCTION : ENVIRONMENTS.DEVELOPMENT;
	}

	// Server-side: check environment variables
	const raw = getEnv([ENV_NEXT_PUBLIC.APP_ENV, 'NODE_ENV'], '');
	return normalizeEnvString(raw) ?? defaultEnv;
}

/** Node/Next-side development check -- delegates to `resolveEnvironment()` so the priority chain has ONE reader. Workers code
 * asks `isWorkerDev(env)` (`utils/config/env-accessors.ts`) instead: that reads the wrangler `environment` var, not `process.env`. */
export function isNodeDevelopment(): boolean {
	return resolveEnvironment() === ENVIRONMENTS.DEVELOPMENT;
}

/** Resolve the storage-gateway WebSocket base URL for the current host, shared by every browser hook opening a WS against
 * PropertyDO / the notifications stream. Hardcoding the gateway host (rather than `window.location.origin`) is what makes the
 * WS handshake reach the gateway worker, since the Next.js app hosts have no ws route. SSR-safe -- returns the dev URL when
 * `window` is undefined. */
export function resolveWsBase(): string {
	if (typeof window === 'undefined') return STORAGE_GATEWAY.DEV_WS_URL;
	return isProductionHostname(window.location?.hostname ?? '') ? STORAGE_GATEWAY.PROD_WS_URL : STORAGE_GATEWAY.DEV_WS_URL;
}

/** Resolve the Federa SITE origin a page on this host links out to (a property's public page opened from an admin
 * surface): production's on a production host, development's otherwise. SSR-safe the same way {@link resolveWsBase} is. */
export function resolveFederaSiteBase(): string {
	if (typeof window === 'undefined') return FEDERA_DEV_URL;
	return isProductionHostname(window.location?.hostname ?? '') ? FEDERA_PROD_URL : FEDERA_DEV_URL;
}
